import { ArrowLeftOutlined } from "@ant-design/icons";
import "@sendbird/uikit-react/dist/index.css";
import { useHistory } from "react-router";
import "../chatStyle.scss";
import { logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "src/constants/firebaseEvents";
import { logProfileNavigated } from "src/containers/workers/logs";
import { WhoCalledRatingPerformanceModal } from "@src/appV2/components/BlockWorkersAndGetDonuts/constants";
import { getLocation } from "@src/appV2/redesign/utils/routes";
import { getChannelTitle } from "../channel";
import { Text, useModalState } from "@clipboard-health/ui-react";
import { useWorker } from "@src/appV2/Worker/api/useWorker";
import { useShiftInfo } from "../utils/useShiftInfo";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, Button, Tooltip, Menu, MenuItem, ButtonGroup } from "@mui/material";
import { ArrowDropDown, Send, PersonOutline } from "@mui/icons-material";
import { useWorkplaceWorker } from "@src/containers/workers/WorkerDetails/useWorkplaceWorker";
import { FacilityStatusObj } from "@src/appV2/redesign/utils/facility";
import {
  NO_MATCHING_QUALIFICATION_TOOLTIP_MESSAGE,
  PENDING_ACCOUNT_TOOLTIP_MESSAGE,
} from "@src/appV2/Calendar/constants";
import { ChatChannel } from "../chat.types";
import { ShiftInviteDialogWrapper } from "@src/containers/Shift/ShiftInvites/ShiftInviteDialogWrapper";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { FACILITY_ROLES, FACILITY_USER_PERMISSIONS } from "@src/constants/facility";
import { useState, useEffect, useRef } from "react";
import { useGetQualifications } from "@src/appV2/Admin/Qualifications/api/useGetQualifications";
import { StarProfessionalChatButton } from "./StarProfessionalChatButton";
import { StarredProfessionalConfirmationModal } from "@src/appV2/Professionals/StarredProfessionals/components/starredProfessionalConfirmationModal";
import { useWorkplaceStarredProfessionals } from "@src/appV2/Professionals/api/starredProfessionals/useWorkplaceStarredProfessionals";
import { usePostStarredProfessional } from "@src/appV2/Professionals/api/starredProfessionals/usePostStarredProfessional";
import { useDeleteStarredProfessional } from "@src/appV2/Professionals/api/starredProfessionals/useDeleteStarredProfessional";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { ShareCredentialsModal } from "@src/appV2/WorkerCredentials/ShareCredentialsModal";
import { ShareCredentialsChatButton } from "@src/containers/chat/components/ShareCredentialsChatButton";
import { useCredentialSharing } from "@src/appV2/Professionals/MyProfessionals/FeatureFlag/useCredentialSharing";
import { areCredentialsMissing } from "@src/appV2/WorkerCredentials/areCredentialsMissing";
import { useGetWorkerCredentials } from "@src/appV2/WorkerCredentials/api/useGetWorkerCredentials";

export interface ChatHeaderProps {
  workerId: string;
  workplaceId: string;
  workplaceUserId: string;
  currentChannel?: ChatChannel;
  backAction: boolean;
  facilityStatus: typeof FacilityStatusObj[keyof typeof FacilityStatusObj];
  isBlocked: boolean;
}

export function ChatHeader(props: ChatHeaderProps) {
  const {
    workerId,
    workplaceId,
    workplaceUserId,
    currentChannel,
    backAction,
    facilityStatus,
    isBlocked,
  } = props;
  const editShiftInviteModalState = useModalState();
  const shareCredentialsModalState = useModalState();
  const { data: worker, isSuccess: isWorkerSuccess } = useWorker(workerId);
  const { showErrorToast } = useToast();
  const {
    isEnabled: isCredentialSharingEnabled,
    isChartingAndTimeClock,
    isChartingOnly,
    isTimeClockOnly,
  } = useCredentialSharing();
  const { data: workerCredentialsResponse, isLoading: isLoadingChartingCredentials } =
    useGetWorkerCredentials({
      filter: { workerIds: workerId, workplaceId },
    });
  const workerCredentials = workerCredentialsResponse?.data[0]?.attributes ?? {};
  const missingCredentials = areCredentialsMissing({
    isChartingAndTimeClock,
    isChartingOnly,
    isTimeClockOnly,
    credentials: { ...workerCredentials, workerId, workplaceId },
  });

  const shiftInfo = useShiftInfo(workplaceId, worker?.qualification);
  const history = useHistory();
  const { admin, user } = useSession();
  const { access = [], permissions = [] } = user ?? {};
  const canPostShiftInvite =
    admin ||
    access.includes(FACILITY_ROLES.ADMIN) ||
    access.includes(FACILITY_ROLES.SHIFT_MANAGEMENT) ||
    permissions.includes(FACILITY_USER_PERMISSIONS.POST_SHIFT_PERMISSION);

  const handleWorkerNameClicked = (withNavigation: boolean) => {
    if (withNavigation) {
      history.push(getLocation("workplaceWorkerDetails", { pathParams: { workerId } }));
    }
    logProfileNavigated({
      workerId,
      workplaceId,
      workplaceUserId,
      location: WhoCalledRatingPerformanceModal.CHAT_CHANNEL,
    });
  };

  const {
    lastWorkedShift,
    workerDetails: workerWorkplaceDetails,
    isLoading: isLoadingWorkplaceWorker,
  } = useWorkplaceWorker({
    workerId,
    workplaceId,
    workplaceUserId,
  });
  const hasWorkedAtWorkplace = isDefined(lastWorkedShift);

  // Get qualification details for display labels
  const { data: qualificationsData } = useGetQualifications();
  const allQualifications = qualificationsData?.data || [];
  const qualificationDisplayLabels = new Map(
    allQualifications.map((q) => [q.attributes.name, q.attributes.displayLabel])
  );

  // Priority order for default qualification selection:
  // 1. Qualification from chat context (agentReq in metadata) if valid
  // 2. Worker's qualification from their last worked shift if valid
  // 3. Worker's preferred qualification if valid
  // 4. First available matching qualification
  const defaultShiftInviteQualification =
    workerWorkplaceDetails?.matchingFacilityQualifications.find(
      (qualification) => qualification === currentChannel?.metadata?.agentReq
    ) ??
    workerWorkplaceDetails?.matchingFacilityQualifications.find(
      (qualification) => qualification === lastWorkedShift?.agentReq
    ) ??
    workerWorkplaceDetails?.matchingFacilityQualifications.find(
      (qualification) => qualification === worker?.qualification
    ) ??
    workerWorkplaceDetails?.matchingFacilityQualifications[0];

  // State for dropdown menu and dialog qualification
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dialogQualification, setDialogQualification] = useState<string | undefined>(undefined);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Reset states when switching chats or default qualification changes
  useEffect(() => {
    setDropdownOpen(false);
    setDialogQualification(undefined);
  }, [workerId, workplaceId, currentChannel?.metadata?.agentReq, defaultShiftInviteQualification]);

  // Available qualifications for dropdown (excluding the default one)
  const availableQualifications =
    workerWorkplaceDetails?.matchingFacilityQualifications?.filter(
      (qual) => qual !== defaultShiftInviteQualification
    ) ?? [];

  const hasMultipleQualifications =
    (workerWorkplaceDetails?.matchingFacilityQualifications?.length ?? 0) > 1;
  const shouldShowSplitButton = hasMultipleQualifications && defaultShiftInviteQualification;

  // Helper functions
  const handleInviteClick = () => {
    setDialogQualification(defaultShiftInviteQualification);
    editShiftInviteModalState.openModal();
    logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
      action: "CHAT_MODAL_OPEN",
      workplaceId,
      workplaceUserId,
      workerType: defaultShiftInviteQualification,
      ...shiftInfo,
    });
  };

  const handleQualificationSelect = (qualification: string) => {
    setDropdownOpen(false);
    setDialogQualification(qualification);

    // Immediately send invite with the selected qualification
    editShiftInviteModalState.openModal();
    logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
      action: "CHAT_MODAL_OPEN",
      workplaceId,
      workplaceUserId,
      workerType: qualification,
      ...shiftInfo,
    });
  };

  const handleDropdownToggle = () => {
    setDropdownOpen((prevOpen) => !prevOpen);
  };

  const handleDropdownClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setDropdownOpen(false);
  };

  const starredProfessionalConfirmationModalState = useModalState();

  const { isStarredProfessionalEnabled, workplaceHasStarredProfessionals, starredProfessionalId } =
    useWorkplaceStarredProfessionals({ workplaceId, workerId });
  const [isStarred, setIsStarred] = useState(
    isDefined(starredProfessionalId) && isStarredProfessionalEnabled
  );
  const { mutateAsync: starProfessional } = usePostStarredProfessional({
    workplaceId,
    professionalId: workerId,
  });
  const { mutateAsync: deleteStarredProfessional } = useDeleteStarredProfessional({
    workplaceId,
    starredProfessionalId: starredProfessionalId ?? "",
  });

  const handleStarProfessional = async () => {
    if (isBlocked) {
      logEvent(APP_V2_USER_EVENTS.STARRED_PROFESSIONAL_ATTEMPTED_BUT_BLOCKED, {
        workerId,
        workplaceId,
        workplaceUserId: user?._id,
      });
      showErrorToast("Professionals on your DNR list cannot be added to your starred list");
      return;
    }
    if (workplaceHasStarredProfessionals) {
      setIsStarred((previous) => !previous);
      const action = isStarred ? deleteStarredProfessional : starProfessional;
      const event = isStarred
        ? APP_V2_USER_EVENTS.REMOVED_STARRED_PROFESSIONAL
        : APP_V2_USER_EVENTS.STARRED_PROFESSIONAL;

      logEvent(event, {
        isStarred,
        workerId,
        workplaceId,
        workplaceUserId: user?._id,
        source: "Chat",
      });
      try {
        await action();
      } catch {
        setIsStarred((previous) => !previous);
      }
    } else {
      logEvent(APP_V2_USER_EVENTS.STARRED_PROFESSIONAL_CONFIRMATION_MODAL_OPENED, {
        workerId,
        workplaceId,
        workplaceUserId: user?._id,
      });
      starredProfessionalConfirmationModalState.openModal();
    }
  };

  const getQualificationDisplayName = (qualification: string) => {
    return qualificationDisplayLabels.get(qualification) || qualification;
  };

  const isDisabledAccountPending = facilityStatus === FacilityStatusObj.PENDING;
  const hasDefaultQualification = isDefined(defaultShiftInviteQualification);
  // If we already have a default qualification, do NOT disable during loading
  const isDisabledLoadingQualification =
    !hasDefaultQualification && isLoadingWorkplaceWorker.workerDetails;
  // Only consider "no matching" when not loading and we truly have none
  const isDisabledNoMatchingQualification =
    !hasDefaultQualification && !isLoadingWorkplaceWorker.workerDetails;
  const isShiftInviteDisabled =
    isDisabledAccountPending || isDisabledNoMatchingQualification || isDisabledLoadingQualification;
  let inviteDisabledTooltip = "";
  if (isDisabledAccountPending) {
    inviteDisabledTooltip = PENDING_ACCOUNT_TOOLTIP_MESSAGE;
  } else if (isDisabledNoMatchingQualification) {
    inviteDisabledTooltip = NO_MATCHING_QUALIFICATION_TOOLTIP_MESSAGE;
  }

  return (
    <div className="custom-header">
      <div className="custom-header-title">
        {backAction && (
          <ArrowLeftOutlined
            className="close-channel"
            onClick={() => history.push(getLocation("facilityChat"))}
          />
        )}
        <Stack direction="column" spacing={0.5}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Text variant="h3" color="text.primary" bold sx={{ pb: 1 }}>
              {getChannelTitle(currentChannel)}
            </Text>
          </Stack>
          <Stack direction="row" spacing={1}>
            {canPostShiftInvite && (
              <Tooltip title={inviteDisabledTooltip} arrow>
                <span>
                  {shouldShowSplitButton ? (
                    <ButtonGroup variant="contained" ref={anchorRef} disableElevation>
                      <Button
                        onClick={handleInviteClick}
                        disabled={isShiftInviteDisabled}
                        sx={{ textTransform: "none" }}
                      >
                        {/* Include Send icon as child instead of passing to Button's startIcon
                        prop to allow for styling overrides. Apply same margins as startIcon */}
                        <Send sx={{ fontSize: 15, mr: 1, ml: -0.5 }} />
                        Invite as {getQualificationDisplayName(defaultShiftInviteQualification)}
                      </Button>
                      <Button
                        size="small"
                        onClick={handleDropdownToggle}
                        disabled={isShiftInviteDisabled}
                        sx={{ px: 1 }}
                      >
                        <ArrowDropDown />
                      </Button>
                    </ButtonGroup>
                  ) : (
                    <Button
                      onClick={handleInviteClick}
                      variant="contained"
                      disabled={isShiftInviteDisabled}
                      sx={{ height: "100%" }}
                    >
                      <Send sx={{ fontSize: 15, mr: 1, ml: -0.5 }} />
                      Invite Professional
                    </Button>
                  )}
                </span>
              </Tooltip>
            )}
            <StarProfessionalChatButton
              isStarred={isStarred}
              visible={isStarredProfessionalEnabled && hasWorkedAtWorkplace}
              handleStarProfessional={handleStarProfessional}
            />
            <ShareCredentialsChatButton
              missingCredentials={missingCredentials}
              visible={isCredentialSharingEnabled}
              isLoading={isLoadingChartingCredentials}
              handleShareCredentials={() => shareCredentialsModalState.openModal()}
            />
            <Button
              onClick={() => handleWorkerNameClicked(true)}
              variant="outlined"
              startIcon={<PersonOutline />}
              sx={{ border: (theme) => `1px solid ${theme.palette.divider}` }}
            >
              View Profile
            </Button>
          </Stack>

          {/* Dropdown Menu */}
          <Menu
            open={dropdownOpen}
            anchorEl={anchorRef.current}
            onClose={handleDropdownClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {availableQualifications.map((qualification) => (
              <MenuItem
                key={qualification}
                onClick={() => handleQualificationSelect(qualification)}
              >
                Invite as {getQualificationDisplayName(qualification)}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </div>
      {isWorkerSuccess &&
        isDefined(shiftInfo) &&
        isDefined(dialogQualification) &&
        editShiftInviteModalState.modalIsOpen && (
          <ShiftInviteDialogWrapper
            workerType={dialogQualification}
            modalState={editShiftInviteModalState}
            shiftInfo={shiftInfo}
            initialActiveState={0}
            initialSelectedWorker={{
              workerId: worker.userId,
              phoneNumber: worker.phone,
              fullName: worker.name,
            }}
          />
        )}
      {isDefined(worker) && (
        <StarredProfessionalConfirmationModal
          isOpen={starredProfessionalConfirmationModalState.modalIsOpen}
          onClose={starredProfessionalConfirmationModalState.closeModal}
          workerName={worker.name}
          workplaceId={workplaceId}
          workerId={workerId}
          key={workerId}
          setIsStarred={setIsStarred}
        />
      )}
      {shareCredentialsModalState.modalIsOpen && (
        <ShareCredentialsModal
          workerId={workerId}
          workplaceId={workplaceId}
          source="workplace-chat"
          workerName={worker?.name}
          onClose={shareCredentialsModalState.closeModal}
          onSuccess={() => {}}
        />
      )}
    </div>
  );
}
